import React, { useCallback, useEffect, useState } from 'react';
import { Gauge, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { errorText, partnerApi } from '../lib/emsportsPartner';
import { Busy, Empty, Field, JsonEditor, Panel, Problem } from './EmSportsBits';
import ConfirmDialog from './ConfirmDialog';

/**
 * EveryMatrix — Trading ve markalar.
 *
 * Trading kuralları **hem vitrini hem kabul edilen bahsi** etkiler: askıya
 * alınan bir seçim oyuncuya kapalı görünür ve o seçime gelen bahis
 * reddedilir. Bu yüzden ekran iki şeyi açıkça yazar: kuralın yayılması bir
 * önbellek turu kadar gecikir (≤ 20 sn, anında değil) ve öncelik sırası
 * dardan genişe doğrudur — sonuç › market › maç › turnuva › spor › merchant.
 * Yazılmasaydı yönetici "kural işlemedi" deyip ikinci kez uygular.
 *
 * Markalar tek bir adresin altında birden çok alt marka çalıştırmak içindir;
 * marka gövdesi merchant ayarlarının **üzerine biner** ve yalnızca dört
 * eksende geçerlidir (tema, görünürlük, risk, ayarlar). Banner marka
 * gövdesine girmez — ayrı uçtan `brand_id` ile bağlanır.
 */

const ACTIONS = [
  { value: 'suspend', label: 'Askıya al (bahis kabul edilmez)' },
  { value: 'hide', label: 'Gizle (vitrinde görünmez)' },
  { value: 'margin', label: 'Marj uygula (oranı düşürür)' },
  { value: 'fixed', label: 'Sabit oran' },
];

const SCOPES = [
  { value: 'merchant', label: 'Tüm kitap' },
  { value: 'sport', label: 'Spor dalı' },
  { value: 'tournament', label: 'Turnuva' },
  { value: 'event', label: 'Maç' },
  { value: 'market', label: 'Market' },
  { value: 'outcome', label: 'Seçim' },
];

const EMPTY_OVERRIDE = {
  scope: 'event', scope_id: '', action: 'suspend', value: '', note: '',
};

const EmSportsTrading = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState('');
  const [overrides, setOverrides] = useState([]);
  const [draft, setDraft] = useState(EMPTY_OVERRIDE);
  const [brands, setBrands] = useState([]);
  const [brandName, setBrandName] = useState('');
  const [brandConfig, setBrandConfig] = useState({});
  // Oran kuralı ve marka silmek geri alınamaz; kural kalktığı anda
  // o kapsamdaki seçimler yeniden oynanabilir olur.
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [overrideRes, brandRes] = await Promise.all([
        partnerApi.overrides().catch((error) => ({ error })),
        partnerApi.brands().catch((error) => ({ error })),
      ]);
      if (overrideRes.error) throw overrideRes.error;
      const rows = overrideRes.data?.overrides || overrideRes.data?.items || [];
      setOverrides(Array.isArray(rows) ? rows : []);
      if (!brandRes.error) {
        const list = brandRes.data?.brands || brandRes.data?.items || [];
        setBrands(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      setProblem(errorText(error, 'Trading bilgileri alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, action, successTitle) => {
    setBusy(key);
    try {
      await action();
      if (successTitle) toast({ title: successTitle });
      await load();
    } catch (error) {
      toast({
        title: 'İşlem tamamlanamadı',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const submitOverride = () => {
    const body = {
      scope: draft.scope,
      action: draft.action,
    };
    if (draft.scope !== 'merchant') body.scope_id = draft.scope_id.trim();
    if (draft.action === 'margin' || draft.action === 'fixed') {
      body.value = Number(draft.value);
    }
    if (draft.note.trim()) body.note = draft.note.trim();
    return run('override:new', async () => {
      await partnerApi.saveOverride(body);
      setDraft(EMPTY_OVERRIDE);
    }, 'Kural sağlayıcıya yazıldı');
  };

  if (loading) return <Busy />;

  const needsValue = draft.action === 'margin' || draft.action === 'fixed';
  const canSubmit = (draft.scope === 'merchant' || draft.scope_id.trim())
    && (!needsValue || Number(draft.value) > 0);

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Canlı oran müdahalesi"
        description={
          'Kural hem oyuncuya gösterilen oranı hem bahsin kabulünü etkiler. '
          + 'Yayılma bir önbellek turu kadar gecikir: en çok 20 saniye, '
          + 'anında değil. Öncelik dardan genişe: seçim › market › maç › '
          + 'turnuva › spor › tüm kitap. Marj oranı düşürür (oran × (1 − %)), '
          + 'taban 1,01’dir. Maç askıya alma kısayolu yoktur: maç kapsamında '
          + 'bir askı kuralı açıp silerek geri alırsınız.'
        }
        actions={(
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={load}
          >
            <RefreshCw size={12} aria-hidden="true" /> Yenile
          </button>
        )}
      >
        {overrides.length ? (
          <ul className="ems-rows">
            {overrides.map((row) => (
              <li
                key={row.id}
                className="ems-row"
              >
                <Gauge size={14} className="ems-credit-pending" aria-hidden="true" />
                <span className="ems-row-title">
                  {SCOPES.find((s) => s.value === row.scope)?.label || row.scope}
                  {row.scope_id ? ` · ${row.scope_id}` : ''}
                </span>
                <span>
                  {ACTIONS.find((a) => a.value === row.action)?.label
                    || row.action}
                  {row.value ? ` · ${row.value}` : ''}
                </span>
                {row.note && (
                  <span className="ems-row-sub">{row.note}</span>
                )}
                <button
                  type="button"
                  className="admin-btn-danger ems-row-end"
                  aria-label={`${row.scope}${row.scope_id ? ` ${row.scope_id}` : ''} kuralını kaldır`}
                  disabled={busy === `override:${row.id}`}
                  onClick={() => setPending({ kind: 'override', row })}
                >
                  <Trash2 size={12} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Etkin bir oran kuralı yok.</Empty>
        )}

        <div className="ems-form">
          <Field label="Kapsam">
            <select
              className="admin-input"
              value={draft.scope}
              onChange={(event) => setDraft(
                (old) => ({ ...old, scope: event.target.value }))}
            >
              {SCOPES.map((scope) => (
                <option key={scope.value} value={scope.value}>
                  {scope.label}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Kimlik"
            hint={draft.scope === 'merchant' ? 'Tüm kitapta gerekmez' : ''}
          >
            <input
              className="admin-input"
              disabled={draft.scope === 'merchant'}
              value={draft.scope_id}
              onChange={(event) => setDraft(
                (old) => ({ ...old, scope_id: event.target.value }))}
            />
          </Field>
          <Field label="İşlem">
            <select
              className="admin-input"
              value={draft.action}
              onChange={(event) => setDraft(
                (old) => ({ ...old, action: event.target.value }))}
            >
              {ACTIONS.map((action) => (
                <option key={action.value} value={action.value}>
                  {action.label}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label={draft.action === 'margin' ? 'Marj (%)' : 'Oran'}
            hint={draft.action === 'margin' ? '0–90 arası' : ''}
          >
            <input
              type="number"
              step="0.01"
              className="admin-input"
              disabled={!needsValue}
              value={draft.value}
              onChange={(event) => setDraft(
                (old) => ({ ...old, value: event.target.value }))}
            />
          </Field>
          <div className="ems-form-wide">
            <button
              type="button"
              className="admin-btn-primary inline-flex items-center gap-2 !py-1 !text-[11px]"
              disabled={!canSubmit || busy === 'override:new'}
              onClick={submitOverride}
            >
              {busy === 'override:new'
                ? <Loader2 size={12} className="animate-spin" />
                : <Plus size={12} />}
              Kuralı uygula
            </button>
          </div>
        </div>
      </Panel>

      <Panel
        title="Markalar"
        description={
          'Tek adresin altında birden çok alt marka çalıştırmak içindir: '
          + 'oyuncu ?brand=<id> ile açılır ve markanın gövdesi merchant '
          + 'ayarlarının üzerine biner. Yalnızca dört eksen geçersiz kılınır '
          + '(tema, görünürlük, risk, ayarlar); banner marka gövdesine '
          + 'girmez, banner kaydına brand_id yazılarak bağlanır.'
        }
      >
        {brands.length ? (
          <ul className="ems-rows">
            {brands.map((row) => (
              <li
                key={row.id}
                className="ems-row"
              >
                <span className="ems-row-title">
                  {row.name || row.id}
                </span>
                <span className="ems-row-sub">#{row.id}</span>
                <span className={row.active === false
                  ? 'ems-credit-none' : 'ems-credit-done'}
                >
                  {row.active === false ? 'Kapalı' : 'Açık'}
                </span>
                <button
                  type="button"
                  className="admin-btn-danger ems-row-end"
                  aria-label={`${row.name || row.id} markasını sil`}
                  disabled={busy === `brand:${row.id}`}
                  onClick={() => setPending({ kind: 'brand', row })}
                >
                  <Trash2 size={12} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Tanımlı alt marka yok.</Empty>
        )}

        <div className="ems-form">
          <Field label="Marka adı">
            <input
              className="admin-input"
              value={brandName}
              onChange={(event) => setBrandName(event.target.value)}
            />
          </Field>
          <Field
            label="Marka gövdesi"
            hint='Örnek: {"settings": {"default_language": "en"}}'
          >
            <JsonEditor value={brandConfig} onChange={setBrandConfig} rows={8} />
          </Field>
          <button
            type="button"
            className="admin-btn-primary inline-flex items-center gap-2 !py-1 !text-[11px]"
            disabled={!brandName.trim() || busy === 'brand:new'}
            onClick={() => run('brand:new', async () => {
              await partnerApi.createBrand({
                name: brandName.trim(), config: brandConfig,
              });
              setBrandName('');
              setBrandConfig({});
            }, 'Marka oluşturuldu')}
          >
            <Plus size={12} /> Marka ekle
          </button>
        </div>
      </Panel>

      <ConfirmDialog
        open={Boolean(pending)}
        title={pending?.kind === 'brand' ? 'Markayı sil' : 'Oran kuralını kaldır'}
        description={pending?.kind === 'brand'
          ? `${pending.row.name || pending.row.id} markası silinecek; o markayla açılan oyuncular merchant ayarlarına döner.`
          : (pending
            ? `${pending.row.scope}${pending.row.scope_id ? ` \u00b7 ${pending.row.scope_id}` : ''} kuralı kaldırılacak. Değişiklik en çok 20 saniye içinde vitrine yansır.`
            : '')}
        confirmLabel={pending?.kind === 'brand' ? 'Sil' : 'Kaldır'}
        tone="danger"
        busy={Boolean(busy)}
        onCancel={() => !busy && setPending(null)}
        onConfirm={async () => {
          const { kind, row } = pending;
          await run(
            `${kind}:${row.id}`,
            () => (kind === 'brand'
              ? partnerApi.deleteBrand(row.id)
              : partnerApi.deleteOverride(row.id)),
            kind === 'brand' ? 'Marka silindi' : 'Kural kaldırıldı',
          );
          setPending(null);
        }}
      />
    </div>
  );
};

export default EmSportsTrading;
